import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { Form } from 'formik';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormInputField, {
  FormInputFieldType,
} from '../../common/components/formFields/FormInputField';
import { useMediaQuery } from '../../common/hooks/useMediaQuery';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import CommonButton from '../../common/components/CommonButton';
import {
  generateDescriptionSuggestion,
  getAvailableDescriptionSuggestionAttempts,
  setDescriptionSuggestion,
  setGeneratingDescription,
  setDescriptionSuggestionAttempts,
} from '../AddVehicleActions';
import { showAlert } from '../../common/components/alert/AlertActions';
import AlertVariant from '../../common/components/alert/AlertVariant';
import { areRequiredBasicDataFieldsFilled } from '../forms/basicDataRequiredFields';
import clipboardIcon from '../../assets/img/icons/clipboard.svg';

function AddDescriptionForm({
  form,
  className,
  disabled = false,
  descriptionError,
  saveDraftRef,
  showSuccessAlertRef,
}) {
  const dispatch = useDispatch();
  const formClassName = classNames('text-left', 'mt-5', className);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const auctionId = form.values?.id;
  const descriptionSuggestion = useSelector((state) =>
    state.addVehicle.get('descriptionSuggestion'),
  );
  const isGeneratingDescription = useSelector((state) =>
    state.addVehicle.get('isGeneratingDescription'),
  );
  const descriptionSuggestionAttempts = useSelector((state) =>
    state.addVehicle.get('descriptionSuggestionAttempts'),
  );
  const showDescriptionTooltip = !!descriptionSuggestion;

  const requiredBasicDataFilled = useMemo(
    () => areRequiredBasicDataFieldsFilled(form.values?.basicData),
    [form.values?.basicData],
  );

  useEffect(() => {
    if (!auctionId || descriptionSuggestionAttempts !== null) return;
    getAvailableDescriptionSuggestionAttempts(auctionId)
      .then((attempts) => dispatch(setDescriptionSuggestionAttempts(attempts)))
      .catch(() => dispatch(setDescriptionSuggestionAttempts(0)));
  }, [auctionId, descriptionSuggestionAttempts, dispatch]);

  const saveSilentlyDraft = async () => {
    if (!saveDraftRef?.current || !showSuccessAlertRef?.current) return null;
    showSuccessAlertRef.current = false;
    try {
      return await saveDraftRef.current({ skipNavigate: true });
    } finally {
      showSuccessAlertRef.current = true;
    }
  };

  const refreshAttempts = async (id) => {
    const targetId = id || auctionId;
    if (!targetId) return;
    const attempts = await getAvailableDescriptionSuggestionAttempts(targetId).catch(
      () => null,
    );
    if (attempts !== null) {
      dispatch(setDescriptionSuggestionAttempts(attempts));
    }
  };

  const handleDescriptionSuggestion = async () => {
    if (isGeneratingDescription) {
      return;
    }

    if (!requiredBasicDataFilled) {
      dispatch(
        showAlert(
          'Please fill in all required vehicle details before generating a description.',
          AlertVariant.DANGER,
        ),
      );
      return;
    }

    dispatch(setGeneratingDescription(true));
    dispatch(setDescriptionSuggestion(null));

    const savedAuctionId = (await saveSilentlyDraft()) || auctionId;

    if (!savedAuctionId) {
      dispatch(setGeneratingDescription(false));
      return;
    }

    try {
      const content = await generateDescriptionSuggestion(savedAuctionId, dispatch);
      if (content) {
        dispatch(setDescriptionSuggestion(content));
      }
    } finally {
      dispatch(setGeneratingDescription(false));
      await refreshAttempts(savedAuctionId);
    }
  };

  const getLabel = (desktopLabel, mobileLabel) => (isDesktop ? desktopLabel : mobileLabel);

  const handleInsertSuggestion = () => {
    form.setFieldValue('descriptionAndSettings.description', descriptionSuggestion);
    dispatch(setDescriptionSuggestion(null));
  };

  const copyDescriptionToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(descriptionSuggestion || '');
      dispatch(showAlert('Description copied to clipboard', AlertVariant.SUCCESS));
    } catch {
      dispatch(showAlert('Could not copy to clipboard', AlertVariant.DANGER));
    }
  };

  const generateDescriptionButtonDisabled = useMemo(
    () =>
      disabled ||
      isGeneratingDescription ||
      !requiredBasicDataFilled ||
      (!!auctionId && descriptionSuggestionAttempts < 1) ||
      showDescriptionTooltip,
    [
      disabled,
      isGeneratingDescription,
      requiredBasicDataFilled,
      auctionId,
      descriptionSuggestionAttempts,
      showDescriptionTooltip,
    ],
  );

  return (
    <Form autoComplete="off" className={formClassName}>
      <Row>
        <Col>
          <FormInputField
            label={getLabel(
              'Describe the vehicle condition in your own words   ',
              'Describe the vehicle condition',
            )}
            type={FormInputFieldType.TEXTAREA}
            id="add-description__user-description"
            name="descriptionAndSettings.description"
            required
            labelVariant="bordered"
            form={form}
            disabled={disabled}
            aria-describedby="add-description__user-description-help"
          />
          {descriptionError && !form.values?.descriptionAndSettings?.description && (
            <div className="field-error-message">
              {typeof descriptionError === 'string' || Array.isArray(descriptionError)
                ? descriptionError
                : 'Please check your description and try again.'}
            </div>
          )}
        </Col>
      </Row>
      <Row className="mt-1 mb-1">
        <Col>
          <CommonButton
            label={isGeneratingDescription ? 'generating...' : 'suggest description'}
            variant={CommonButtonVariants.SECONDARY}
            className={`suggest-description-btn ${generateDescriptionButtonDisabled ? 'suggest-description-btn--disabled' : ''}`}
            handleClick={handleDescriptionSuggestion}
            disabled={generateDescriptionButtonDisabled}
            aria-label="suggest description"
          />
          {descriptionSuggestionAttempts === 0 && (
            <p className="settings-section-header-description mb-2 mt-2">
              You have reached the maximum number of description suggestions for this auction.
            </p>
          )}
        </Col>
      </Row>
      {showDescriptionTooltip && (
        <Row className="mt-4 mb-1">
          <Col>
            <div className="suggest-description-tooltip">
              <div className="suggest-description-tooltip__top">
                <p className="suggest-description-tooltip__title">Generated result</p>
                <button type="button" onClick={copyDescriptionToClipboard}>
                  <img src={clipboardIcon} alt="clipboard" />
                </button>
              </div>
              <div className="suggest-description-tooltip__content">
                {descriptionSuggestion}
              </div>
              <div className="suggest-description-tooltip__buttons">
                <CommonButton
                  label="insert description"
                  handleClick={handleInsertSuggestion}
                />
                <CommonButton
                  label="Try again"
                  className={`suggest-description-btn--cancel ${isGeneratingDescription || !requiredBasicDataFilled || descriptionSuggestionAttempts < 1 ? 'suggest-description-btn--disabled' : ''}`}
                  disabled={
                    isGeneratingDescription ||
                    !requiredBasicDataFilled ||
                    descriptionSuggestionAttempts < 1
                  }
                  variant={CommonButtonVariants.SECONDARY}
                  handleClick={handleDescriptionSuggestion}
                />
              </div>
            </div>
          </Col>
        </Row>
      )}
      <Row className="mt-4">
        <Col>
          <FormInputField
            label={getLabel(
              'Please add a default text that will be added to all your vehicles listings',
              'Default text for all your vehicles listings',
            )}
            type={FormInputFieldType.TEXTAREA}
            id="add-description__user-default-description"
            name="descriptionAndSettings.defaultDescription"
            labelVariant="bordered"
            form={form}
            disabled={disabled}
            aria-describedby="add-description__user-default-description-help"
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <FormInputField
            label={getLabel(
              'Vehicle highlighted info – will be visible on listing preview',
              'Vehicle highlighted info',
            )}
            type={FormInputFieldType.TEXT}
            id="add-description__user-highlighted-info"
            name="descriptionAndSettings.highlightedInfo"
            labelVariant="bordered"
            form={form}
            disabled={disabled}
            aria-describedby="add-description__user-highlighted-info-help"
            maxLength={30}
            lengthCounter
          />
        </Col>
      </Row>
    </Form>
  );
}

AddDescriptionForm.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.object,
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  descriptionError: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  saveDraftRef: PropTypes.shape({ current: PropTypes.func }),
  showSuccessAlertRef: PropTypes.shape({ current: PropTypes.bool }),
};

AddDescriptionForm.defaultProps = {
  className: '',
  disabled: false,
  descriptionError: null,
  saveDraftRef: null,
  showSuccessAlertRef: null,
};

export default AddDescriptionForm;
